import { useEffect, useState } from 'react';

/**
 * Custom hook to determine if the current device is a touch-enabled device.
 *
 * This hook checks for touch capabilities by examining the `ontouchstart` event
 * in the window object and the touch point properties in the navigator object.
 * It updates the state whenever the window is resized to ensure the touch capability
 * status is current.
 *
 * @returns {boolean} - Returns `true` if the device is touch-enabled, otherwise `false`.
 */
const useIsTouchViewportDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const checkTouchDevice = () => {
    const hasTouchPoints =
      'ontouchstart' in window ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0);

    if (!hasTouchPoints) return false;

    if (window.matchMedia) {
      return window.matchMedia('(pointer: coarse)').matches;
    }

    return hasTouchPoints;
  };

  useEffect(() => {
    const updateIsTouchDevice = () => {
      setIsTouchDevice(checkTouchDevice());
    };

    updateIsTouchDevice();
    window.addEventListener('resize', updateIsTouchDevice);

    return () => {
      window.removeEventListener('resize', updateIsTouchDevice);
    };
  }, []);

  return isTouchDevice;
};

export { useIsTouchViewportDevice };
